"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { useParams } from "react-router-dom"
import { api } from "../../services/api"
import { toast } from 'react-toastify'

export default function MissionDetails() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [price, setPrice] = useState("")
  const [saving, setSaving] = useState(false)
  const [progressSaving, setProgressSaving] = useState(false)
  const [offers, setOffers] = useState([])
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentOffer, setCurrentOffer] = useState(null)
  const [counterOffer, setCounterOffer] = useState("")
  const [message, setMessage] = useState("")
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const socketRef = useRef(null)

  // Fonction pour charger les offres d'un devis
  const loadQuoteOffers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getQuoteOffers(id);
      const offersData = Array.isArray(response) ? response : (response?.data || []);
      
      if (offersData.length > 0) {
        setOffers(offersData);
        
        // Mettre à jour l'offre courante (la plus récente)
        const latestOffer = [...offersData]
          .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))[0];
          
        if (latestOffer) {
          setCurrentOffer(latestOffer);
        }
      }
      console.error('Erreur lors du chargement des offres:', error);
    } finally {
      setLoading(false);
    }

  }, [id]);

  // Effet pour le polling des offres et des données de la mission
  useEffect(() => {
    if (!id) return;
    
    let isMounted = true;
    
    // Fonction pour charger les données de la mission
    const fetchMissionData = async () => {
      try {
        const response = await api.get(`/missions/${id}`);
        if (!isMounted) return;
        
        const missionData = response.data;
        
        // Vérifier s'il y a de nouvelles offres
        if (data && missionData.offers && data.offers && 
            missionData.offers.length > data.offers.length) {
          // Afficher une notification pour les nouvelles offres
          const newOffersCount = missionData.offers.length - data.offers.length;
          
          if (Notification.permission === 'granted') {
            new Notification('Nouvelles offres disponibles', {
              body: `${newOffersCount} nouvelle(s) offre(s) reçue(s)`,
              icon: '/logo.png'
            });
          } else if (Notification.permission !== 'denied') {
            Notification.requestPermission();
          }
        }
        
        setData(missionData);
        setLastUpdate(new Date());
      } catch (err) {
        console.error('Erreur lors du chargement des données de la mission:', err);
      }
    };
    
    // Fonction pour charger les offres
    const fetchOffers = async () => {
      try {
        if (!isMounted) return;
        
        // Sauvegarder l'ancien nombre d'offres pour la comparaison
        const oldOffersCount = offers.length;
        
        // Charger les nouvelles offres
        await loadQuoteOffers();
        
        // Vérifier si de nouvelles offres sont arrivées
        if (offers.length > oldOffersCount && oldOffersCount > 0) {
          const newOffersCount = offers.length - oldOffersCount;
          toast.info(`Nouvelle${newOffersCount > 1 ? 's' : ''} proposition${newOffersCount > 1 ? 's' : ''} reçue${newOffersCount > 1 ? 's' : ''} !`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      } catch (err) {
        console.error('Erreur lors du chargement des offres:', err);
        toast.error('Erreur lors du chargement des offres', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    };
    
    fetchMissionData();
    fetchOffers();

    // Fallback lent si le WebSocket est indisponible (sinon negotiation:update suffit).
    const fallbackIntervalId = setInterval(() => {
      if (!isMounted) return;
      fetchMissionData();
      fetchOffers();
    }, 120000);

    return () => {
      isMounted = false;
      clearInterval(fallbackIntervalId);
    };
  }, [id, loadQuoteOffers]);
  
  // Effet pour demander la permission des notifications
  useEffect(() => {
    if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  }, []);
  
  useEffect(() => {
    if (id) {
      loadQuoteOffers();
    }
  }, [id, loadQuoteOffers]);

  // Effet pour gérer la connexion WebSocket
  useEffect(() => {
    if (!id) return;

    let isMounted = true;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    let reconnectTimeout;

    // Fonction pour établir la connexion WebSocket
    const setupWebSocket = () => {
      console.log('[MissionDetails] Initialisation de la connexion WebSocket...');
      
      if (socketRef.current) {
        console.log('[MissionDetails] Nettoyage de la connexion WebSocket existante');
        socketRef.current(); // Nettoyer la connexion existante
      }

      const handleUpdate = (data) => {
        if (!isMounted) return;
        console.log('[MissionDetails] Nouvelle mise à jour en temps réel:', data);
        
        // Mettre à jour les offres
        if (data.offer) {
          setOffers(prevOffers => {
            // Filtrer les offres optimistes
            const filteredOffers = prevOffers.filter(o => !o.isOptimistic);
            
            // Vérifier si l'offre existe déjà
            const existingIndex = filteredOffers.findIndex(o => o.id === data.offer.id);
            
            let newOffers;
            if (existingIndex >= 0) {
              // Mettre à jour l'offre existante
              newOffers = [...filteredOffers];
              newOffers[existingIndex] = {
                ...newOffers[existingIndex],
                ...data.offer,
                amount: data.offer.amount !== undefined ? data.offer.amount : newOffers[existingIndex].amount,
                status: data.offer.status || newOffers[existingIndex].status,
                message: data.offer.message || newOffers[existingIndex].message,
                updated_at: data.offer.updated_at || new Date().toISOString()
              };
            } else {
              // Ajouter la nouvelle offre
              newOffers = [data.offer, ...filteredOffers];
            }
            
            console.log('[MissionDetails] Mise à jour des offres:', newOffers);
            return newOffers;
          });

          // Mettre à jour l'offre courante
          setCurrentOffer(prev => {
            if (!prev || 
                (data.offer.updated_at > prev.updated_at) || 
                (data.offer.id === prev.id)) {
              const updated = {
                ...prev,
                ...data.offer,
                amount: data.offer.amount !== undefined ? data.offer.amount : (prev?.amount || 0),
                status: data.offer.status || prev?.status,
                message: data.offer.message || prev?.message,
                updated_at: data.offer.updated_at || new Date().toISOString()
              };
              console.log('[MissionDetails] Mise à jour de l\'offre courante:', updated);
              return updated;
            }
            return prev;
          });
        }

        // Mettre à jour les données du devis
        if (data.quote) {
          console.log('[MissionDetails] Mise à jour des données du devis:', data.quote);
          setData(prev => ({
            ...prev,
            ...data.quote,
            status: data.quote.status || prev.status,
            price_cents: data.quote.price_cents || prev.price_cents,
            updated_at: data.quote.updated_at || prev.updated_at
          }));
        }
      };

      const cleanup = api.connectToNegotiationUpdates(id, handleUpdate);
      
      // Fonction de reconnexion
      const scheduleReconnect = () => {
        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000); // Exponential backoff, max 30s
          console.log(`[MissionDetails] Tentative de reconnexion dans ${delay}ms (${reconnectAttempts}/${maxReconnectAttempts})`);
          
          reconnectTimeout = setTimeout(() => {
            if (isMounted) {
              console.log('[MissionDetails] Tentative de reconnexion...');
              setupWebSocket();
            }
          }, delay);
        } else {
          console.error('[MissionDetails] Nombre maximum de tentatives de reconnexion atteint');
        }
      };

      // Gestion des erreurs de connexion
      const handleConnectError = (error) => {
        console.error('[MissionDetails] Erreur de connexion WebSocket:', error);
        scheduleReconnect();
      };

      // Nettoyage
      return () => {
        console.log('[MissionDetails] Nettoyage de la connexion WebSocket');
        if (reconnectTimeout) clearTimeout(reconnectTimeout);
        if (cleanup) cleanup();
      };
    };

    // Démarrer la connexion WebSocket
    const cleanup = setupWebSocket();
    socketRef.current = cleanup;

    // Nettoyer à la fin
    return () => {
      console.log('[MissionDetails] Démontage du composant, nettoyage des ressources');
      isMounted = false;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (cleanup) cleanup();
    };
  }, [id]);

  // Fonction pour vérifier l'authentification
  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = `/login?redirect=/demenageur/missions/${id}`;
      return false;
    }
    return true;
  }, [id]);

  // Fonction pour charger les données du devis
  const loadQuoteData = useCallback(async () => {
    try {
      const resp = await api.fetchMoverQuoteById(id);
      const quoteData = resp?.data;
      
      if (!quoteData) {
        setError('Devis non trouvé');
        return null;
      }
      
      setData(quoteData);
      console.log('Quote data:', quoteData);
      return quoteData;
    } catch (error) {
      console.error('Error loading quote data:', error);
      setError('Erreur lors du chargement du devis');
      return null;
    }
  }, [id]);

  // Fonction pour vérifier l'assignation du déménageur
  const checkAssignment = useCallback(async (quoteData) => {
    try {
      const isAssigned = await api.isAssignedToQuote(id);
      console.log('Is current user assigned to quote?', isAssigned);
      
      if (!isAssigned && quoteData.status !== 'pending') {
        setError('Ce devis n\'est plus disponible pour la négociation.');
        return false;
      }
      
      return isAssigned;
    } catch (error) {
      console.error('Error checking assignment:', error);
      // On continue même en cas d'erreur de vérification d'assignation
      return false;
    }
  }, [id]);

  // Chargement initial des données
  useEffect(() => {
    if (!id) return;
    
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        
        // 1. Vérifier l'authentification
        if (!checkAuth()) return;
        
        // 2. Charger les données du devis
        const quoteData = await loadQuoteData();
        if (!quoteData) {
          setLoading(false);
          return;
        }
        
        // 3. Vérifier l'assignation (sans bloquer le chargement)
        await checkAssignment(quoteData);
        
        // 4. Charger les offres pour ce devis
        try {
          console.log('Chargement des offres pour le devis:', id);
          const offersResp = await api.getQuoteOffers(id);
          console.log('Réponse complète des offres:', offersResp);
          
          // Vérifier la structure de la réponse
          const offersData = Array.isArray(offersResp) ? offersResp : (offersResp?.data || []);
          console.log('Offres extraites:', offersData);
          
          if (offersData && offersData.length > 0) {
            console.log(`${offersData.length} offres trouvées`);
            setOffers(offersData);
            
            // Trouver la dernière offre en attente ou la plus récente
            const latestOffer = [...offersData]
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
              
            if (latestOffer) {
              console.log('Dernière offre trouvée:', latestOffer);
              setCurrentOffer(latestOffer);
              setCounterOffer(String(latestOffer.amount || ''));
            }
          } else {
            console.log('Aucune offre trouvée dans la réponse');
            setOffers([]);
          }
        } catch (offerError) {
          console.error('Error loading offers:', offerError);
          setError(offerError.message || 'Erreur lors du chargement des offres');
        }
      } catch (error) {
        console.error('Error in load:', error);
        // Si l'erreur est liée à l'authentification, rediriger vers la page de connexion
        if (error.message.includes('401') || error.message.includes('token') || error.message.includes('authentification')) {
          window.location.href = `/login?redirect=/demenageur/missions/${id}`;
          return;
        }
        setError('Erreur lors du chargement des données du devis: ' + (error.message || 'Erreur inconnue'));
      } finally {
        setLoading(false);
      }
    };
    
    if (checkAuth()) {
      load();
    } else {
      window.location.href = `/login?redirect=/demenageur/missions/${id}`;
    }
  }, [id])
  
  // WebSocket pour les mises à jour en temps réel
  useEffect(() => {
    let socketCleanup = null
    
    const setupWebSocket = async () => {
      try {
        socketCleanup = await api.connectToNegotiationUpdates(id, (data) => {
          console.log('Nouvelle mise à jour WebSocket reçue:', data);
          
          // Si c'est une mise à jour d'offre
          if (data.offer) {
            const updatedOffer = data.offer;
            
            // Mettre à jour la liste des offres
            setOffers(prevOffers => {
              const existingIndex = prevOffers.findIndex(o => o.id === updatedOffer.id);
              let newOffers;
              
              if (existingIndex >= 0) {
                // Mettre à jour l'offre existante
                newOffers = [...prevOffers];
                newOffers[existingIndex] = updatedOffer;
              } else {
                // Ajouter la nouvelle offre au début du tableau
                newOffers = [updatedOffer, ...prevOffers];
              }
              
              console.log('Mise à jour des offres:', newOffers);
              return newOffers;
            });

            // Mettre à jour l'offre courante si nécessaire
            setCurrentOffer(prev => {
              if ((prev && prev.id === updatedOffer.id) || 
                  (!prev && updatedOffer.status === 'pending')) {
                console.log('Mise à jour de l\'offre courante:', updatedOffer);
                return updatedOffer;
              }
              return prev;
            });
          }
          
          // Mettre à jour les données du devis si nécessaire
          if (data.quote) {
            console.log('Mise à jour des données du devis:', data.quote);
            setData(prev => ({
              ...prev,
              ...data.quote,
              status: data.quote.status || prev?.status
            }));
          }
        })
      } catch (e) {
        console.error("Erreur WebSocket:", e)
      }
    }
  
    setupWebSocket()
  
    return () => {
      if (socketCleanup) socketCleanup()
    }
  }, [id])

  // Fonction pour recharger les offres depuis le serveur
  const reloadOffers = useCallback(async () => {
    try {
      console.log('Rechargement des offres depuis le serveur...');
      const response = await api.getQuoteOffers(id);
      const offersData = Array.isArray(response) ? response : (response?.data || []);
      
      if (offersData.length > 0) {
        console.log(`${offersData.length} offres rechargées`);
        setOffers(offersData);
        
        // Mettre à jour l'offre courante
        const latestOffer = [...offersData]
          .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))[0];
          
        if (latestOffer) {
          setCurrentOffer(latestOffer);
        }
      }
    } catch (error) {
      console.error('Erreur lors du rechargement des offres:', error);
    }
  }, [id]);

  // Gestion de la soumission d'une contre-offre
  const handleSubmitCounterOffer = async () => {
    if (!counterOffer || isSubmittingOffer) return;
    
    try {
      setIsSubmittingOffer(true);
      setIsProcessing(true);
      const amount = parseFloat(counterOffer);
      
      if (isNaN(amount) || amount <= 0) {
        console.warn('Veuillez entrer un montant valide');
        return;
      }
      
      // Créer un ID temporaire pour l'optimistic UI
      const tempId = `temp-${Date.now()}`;
      
      // Créer l'objet d'offre optimiste
      const optimisticOffer = {
        id: tempId,
        quote_id: id,
        user_id: 'current', // Le backend remplacera par l'ID réel
        amount: amount,
        status: 'pending',
        message: message || (currentOffer ? "Voici ma contre-proposition de prix." : "Voici ma proposition de prix."),
        user_type: 'mover',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        isOptimistic: true
      };
      
      // Mise à jour optimiste de l'interface
      setOffers(prev => [optimisticOffer, ...prev]);
      setCurrentOffer(optimisticOffer);
      
      // Mise à jour optimiste du statut du devis
      setData(prev => ({
        ...prev,
        status: 'under_negotiation',
        updated_at: new Date().toISOString()
      }));
      
      // Vérifier s'il y a déjà une offre en attente de l'utilisateur actuel
      const pendingOffers = offers.filter(offer => 
        offer.status === 'pending' && 
        offer.user_type === 'mover' &&
        !offer.isOptimistic // Ne pas compter les offres optimistes
      );
      
      // Rejeter les offres en attente existantes
      for (const offer of pendingOffers) {
        try {
          await api.respondToOffer(id, offer.id, {
            status: 'rejected',
            message: 'Remplacée par une nouvelle offre'
          });
        } catch (err) {
          console.error('Erreur lors du rejet de l\'offre existante:', err);
          // Continuer même en cas d'erreur
        }
      }
      
      // Soumettre la nouvelle offre
      const response = await api.submitQuoteOffer(id, {
        amount: amount,
        message: message || (currentOffer ? "Voici ma contre-proposition de prix." : "Voici ma proposition de prix."),
      });
      
      // Vérifier si la réponse contient une erreur
      if (response.data?.error) {
        console.warn('Erreur lors de la création de l\'offre:', response.data.error);
        // On garde l'offre optimiste et on laissera le rechargement automatique s'en occuper
      } else {
        // Mettre à jour avec la réponse du serveur
        const newOffer = response.data || {
          id: `temp-${Date.now()}`,
          quote_id: id,
          amount: amount,
          message: message || (currentOffer ? "Voici ma contre-proposition de prix." : "Voici ma proposition de prix."),
          status: 'pending',
          user_type: 'mover',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          isOptimistic: true
        };
        
        setOffers(prev => [newOffer, ...prev.filter(o => o.id !== tempId && o.id !== newOffer.id)]);
        setCurrentOffer(newOffer);
        setMessage("");
        setCounterOffer("");
        
        // Recharger immédiatement les offres depuis le serveur
        await reloadOffers();
        
        // Vérifier à nouveau après un court délai pour s'assurer que tout est synchronisé
        setTimeout(() => {
          reloadOffers();
        }, 500);
      }
    } catch (error) {
      console.error('Error submitting counter offer:', error);
      
      if (!error.message.includes('déjà une offre en attente')) {
        // Pour les erreurs autres que "offre en attente", on affiche un message dans la console
        console.warn('Erreur lors de la soumission, mais l\'offre est conservée localement:', error);
      }
      // Dans tous les cas, on tente de recharger les offres pour être à jour
      reloadOffers();
    } finally {
      setIsSubmittingOffer(false);
      setIsProcessing(false);
    }
  }
  
  // Accepter une offre
  const handleAcceptOffer = async (offerId) => {
    if (!window.confirm("Êtes-vous sûr d'accepter cette offre ? Cette action est définitive.")) {
      return;
    }
    
    try {
      setIsSubmittingOffer(true);
      
      // 1. Trouver l'offre à accepter
      const offerToAccept = offers.find(offer => offer.id === offerId);
      if (!offerToAccept) {
        throw new Error("Offre non trouvée");
      }
      
      // 2. Mettre à jour le statut du devis via l'API
      const updateResponse = await api.updateMoverQuoteById(id, {
        status: 'accepted',
        price_tnd: offerToAccept.amount,
      });
      
      if (!updateResponse || !updateResponse.success) {
        throw new Error(updateResponse?.message || "Échec de la mise à jour du devis");
      }
      
      // 3. Mettre à jour le statut de l'offre via l'API
      try {
        await api.respondToOffer(id, offerId, {
          status: 'accepted',
          message: "J'accepte votre offre.",
        });
      } catch (offerError) {
        console.error('Erreur lors de la mise à jour du statut de l\'offre:', offerError);
        // On continue quand même car le devis principal a été mis à jour
      }
      
      // 4. Mettre à jour l'état local des offres
      setOffers(prev => 
        prev.map(offer => 
          offer.id === offerId 
            ? { ...offer, status: 'accepted', updated_at: new Date().toISOString() }
            : { ...offer, status: 'rejected' } // Rejeter les autres offres
        )
      );
      
      // 5. Mettre à jour les données principales
      if (data) {
        setData(prev => ({
          ...prev,
          status: 'accepted',
          price_cents: Math.round(offerToAccept.amount * 100), // Convertir en centimes
          updated_at: new Date().toISOString(),
        }));
      }
      
      // 6. Afficher un message de succès
      alert('Offre acceptée avec succès !');
      
      // 7. Recharger les données pour s'assurer que tout est à jour
      try {
        const resp = await api.fetchMoverQuoteById(id);
        if (resp?.data) {
          setData(resp.data);
        }
      } catch (refreshError) {
        console.error('Erreur lors du rechargement des données:', refreshError);
      }
      
      // La création du move est déjà gérée par le backend lors de l'acceptation de l'offre
      // Aucune action supplémentaire nécessaire ici
      
      alert("Offre acceptée avec succès ! Le devis a été converti en mission.")
    } catch (error) {
      console.error('Error accepting offer:', error)
      alert(error.message || "Erreur lors de l'acceptation de l'offre")
    } finally {
      setIsSubmittingOffer(false)
    }
  }
  
  // Refuser une offre
  const handleRejectOffer = async (offerId) => {
    const rejectMessage = window.prompt("Veuillez indiquer la raison de votre refus :")
    if (rejectMessage === null) return // Annulation par l'utilisateur
    
    try {
      setIsSubmittingOffer(true)
      await api.respondToOffer(id, offerId, {
        status: 'rejected',
        message: rejectMessage
      })
      
      // Mettre à jour les offres avec le statut rejeté
      setOffers(prev => 
        prev.map(offer => 
          offer.id === offerId 
            ? { ...offer, status: 'rejected', updated_at: new Date().toISOString() }
            : offer
        )
      )
      
      // Si c'était l'offre courante, la réinitialiser
      if (currentOffer?.id === offerId) {
        setCurrentOffer(null)
      }
      
      alert("Offre refusée avec succès.")
    } catch (error) {
      console.error('Error rejecting offer:', error)
      alert(error.message || "Erreur lors du refus de l'offre")
    } finally {
      setIsSubmittingOffer(false)
    }
  }

  if (loading)
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="text-white text-lg">Chargement des détails...</span>
        </div>
      </div>
    )

  if (error)
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6 max-w-md">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
              <span className="text-red-400 text-xl">⚠️</span>
            </div>
            <div>
              <h3 className="text-red-400 font-semibold">Erreur de chargement</h3>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        </div>
      </div>
    )

  if (!data)
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-gray-400 text-2xl">📋</span>
          </div>
          <h3 className="text-white font-semibold mb-2">Aucune donnée disponible</h3>
          <p className="text-gray-400">Cette mission n'existe pas ou a été supprimée.</p>
        </div>
      </div>
    )

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        bg: "bg-yellow-500/20",
        text: "text-yellow-400",
        border: "border-yellow-500/30",
        icon: "⏳",
        label: "En attente",
      },
      accepted: {
        bg: "bg-blue-500/20",
        text: "text-blue-400",
        border: "border-blue-500/30",
        icon: "✅",
        label: "Acceptée",
      },
      confirmed: {
        bg: "bg-blue-500/20",
        text: "text-blue-400",
        border: "border-blue-500/30",
        icon: "✅",
        label: "Confirmée",
      },
      in_progress: {
        bg: "bg-purple-500/20",
        text: "text-purple-400",
        border: "border-purple-500/30",
        icon: "🚚",
        label: "En cours",
      },
      completed: {
        bg: "bg-green-500/20",
        text: "text-green-400",
        border: "border-green-500/30",
        icon: "🏠",
        label: "Terminée",
      },
    }
    return (
      configs[status] || {
        bg: "bg-gray-500/20",
        text: "text-gray-400",
        border: "border-gray-500/30",
        icon: "❓",
        label: status || "Inconnu",
      }
    )
  }

  // Fonction utilitaire pour obtenir le style d'une offre en fonction de son statut
  const getOfferStatusStyle = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-500/10 border-green-500/30';
      case 'rejected':
        return 'bg-red-500/10 border-red-500/30 opacity-70';
      case 'pending':
        return 'bg-yellow-500/10 border-yellow-500/30';
      default:
        return 'bg-gray-800/50 border-gray-700/50';
    }
  };

  // Fonction utilitaire pour obtenir le style du badge de statut
  const getOfferStatusBadgeStyle = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-500/20 text-green-400';
      case 'rejected':
        return 'bg-red-500/20 text-red-400';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  // Fonction utilitaire pour obtenir le libellé du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case 'accepted':
        return 'Acceptée';
      case 'rejected':
        return 'Refusée';
      case 'pending':
        return 'En attente';
      default:
        return status;
    }
  };

  const statusConfig = getStatusConfig(data.status)

  return (
    <div className="min-h-screen bg-gray-950 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700/50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Détails de la mission</h1>
              <div className="flex items-center space-x-4">
                <span className="text-yellow-500 font-mono text-lg">#{data.id}</span>
                <div
                  className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}
                >
                  <span>{statusConfig.icon}</span>
                  <span className="font-semibold">{statusConfig.label}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-gray-400 text-sm">Prix estimé</div>
                <div className="text-2xl font-bold text-yellow-500">
                  {data.price_cents ? `${(data.price_cents / 100).toFixed(2)} TND` : "Non défini"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Main Details Card */}
          <div className="xl:col-span-2">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
              {/* Route Header */}
              <div className="bg-gradient-to-r from-yellow-500/10 to-yellow-600/5 border-b border-yellow-500/20 p-6">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <span className="text-yellow-400 text-xl">🚛</span>
                  </div>
                  <div>
                    <div className="text-yellow-400 text-sm font-medium">Itinéraire</div>
                    <div className="text-white text-xl font-bold">
                      {data.from_address || "Adresse de départ"} → {data.to_address || "Adresse d'arrivée"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 p-4 bg-gray-800/50 rounded-xl border border-gray-700/30">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <span className="text-blue-400">📅</span>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm">Date du déménagement</div>
                        <div className="text-white font-semibold">
                          {data.move_date
                            ? new Date(data.move_date).toLocaleDateString("fr-FR", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "Non définie"}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 p-4 bg-gray-800/50 rounded-xl border border-gray-700/30">
                      <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                        <span className="text-green-400">📦</span>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm">Volume</div>
                        <div className="text-white font-semibold">{data.volume_m3 || "0"} m³</div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 p-4 bg-gray-800/50 rounded-xl border border-gray-700/30">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                        <span className="text-purple-400">🚚</span>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm">Type de camion</div>
                        <div className="text-white font-semibold">{data.truck_type || "Non spécifié"}</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 p-4 bg-gray-800/50 rounded-xl border border-gray-700/30">
                      <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                        <span className="text-orange-400">🏠</span>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm">Type de logement</div>
                        <div className="text-white font-semibold">{data.housing_type || "Non spécifié"}</div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 p-4 bg-gray-800/50 rounded-xl border border-gray-700/30">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <span className="text-red-400">🏢</span>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm">Étage</div>
                        <div className="text-white font-semibold">
                          {data.floor ?? "Non spécifié"}
                          {typeof data.has_elevator === "boolean" && (
                            <span
                              className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                data.has_elevator ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                              }`}
                            >
                              {data.has_elevator ? "Avec ascenseur" : "Sans ascenseur"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {data.description && (
                  <div className="mt-6 p-4 bg-gray-800/30 rounded-xl border border-gray-700/30">
                    <div className="text-gray-400 text-sm mb-2">Description</div>
                    <div className="text-white">{data.description}</div>
                  </div>
                )}

                {/* Services */}
                {data.services &&
                  (() => {
                    let services = {}
                    try {
                      services = typeof data.services === "string" ? JSON.parse(data.services) : data.services
                    } catch (e) {
                      services = {}
                    }

                    const serviceConfig = {
                      cartons: { label: "Fourniture cartons", icon: "📦", color: "blue" },
                      emballage: { label: "Service emballage", icon: "🎁", color: "green" },
                      nettoyage: { label: "Service nettoyage", icon: "🧹", color: "purple" },
                      gardeMeubles: { label: "Garde-meubles", icon: "🏪", color: "orange" },
                      montageMeubles: { label: "Montage meubles", icon: "🔧", color: "red" },
                    }

                    const requestedServices = Object.entries(services).filter(([key, value]) => value === true)
                    const notRequestedServices = Object.entries(services).filter(([key, value]) => value === false)

                    return (
                      <div className="mt-6 p-4 bg-gray-800/30 rounded-xl border border-gray-700/30">
                        <div className="text-gray-400 text-sm mb-4 flex items-center space-x-2">
                          <span>🛠️</span>
                          <span>Services demandés</span>
                        </div>

                        {requestedServices.length > 0 && (
                          <div className="mb-4">
                            <div className="text-green-400 text-xs font-medium mb-2 uppercase tracking-wide">
                              Services inclus
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {requestedServices.map(([key, value]) => {
                                const config = serviceConfig[key] || { label: key, icon: "⚙️", color: "gray" }
                                return (
                                  <div
                                    key={key}
                                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
                                      config.color === "blue"
                                        ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                                        : config.color === "green"
                                          ? "bg-green-500/20 text-green-400 border-green-500/30"
                                          : config.color === "purple"
                                            ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
                                            : config.color === "orange"
                                              ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
                                              : config.color === "red"
                                                ? "bg-red-500/20 text-red-400 border-red-500/30"
                                                : "bg-gray-500/20 text-gray-400 border-gray-500/30"
                                    }`}
                                  >
                                    <span className="text-sm">{config.icon}</span>
                                    <span className="text-sm font-medium">{config.label}</span>
                                    <span className="text-xs">✓</span>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}

                        {notRequestedServices.length > 0 && (
                          <div>
                            <div className="text-gray-500 text-xs font-medium mb-2 uppercase tracking-wide">
                              Services non demandés
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {notRequestedServices.map(([key, value]) => {
                                const config = serviceConfig[key] || { label: key, icon: "⚙️", color: "gray" }
                                return (
                                  <div
                                    key={key}
                                    className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-700/30 text-gray-500 border border-gray-600/30"
                                  >
                                    <span className="text-sm opacity-50">{config.icon}</span>
                                    <span className="text-sm">{config.label}</span>
                                    <span className="text-xs">✗</span>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}

                        {requestedServices.length === 0 && notRequestedServices.length === 0 && (
                          <div className="text-center py-4">
                            <div className="text-gray-500 text-sm">Aucun service spécifié</div>
                          </div>
                        )}
                      </div>
                    )
                  })()}
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            {/* Section de négociation */}
            {(data?.status === "pending" || data?.status === "under_negotiation") && (
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500/10 to-blue-600/5 border-b border-blue-500/20 p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <span className="text-blue-400">🤝</span>
                    </div>
                    <div className="text-blue-400 font-semibold">
                      {data?.status === 'under_negotiation' ? 'Négociation en cours' : 'Faire une offre'}
                    </div>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  {/* Historique des offres */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-300">Historique des propositions</h4>
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full">
                        {offers.length} {offers.length > 1 ? 'propositions' : 'proposition'}
                      </span>
                    </div>
                    
                    {offers.length > 0 ? (
                      <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                        {[...offers]
                          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                          .map((offer, index, array) => (
                            <div 
                              key={offer.id} 
                              className={`relative p-4 rounded-xl ${getOfferStatusStyle(offer.status)} border-l-4 ${
                                offer.user_type === 'mover' 
                                  ? 'border-l-blue-500' 
                                  : 'border-l-yellow-500'
                              }`}
                            >
                              {/* En-tête de l'offre */}
                              <div className="flex justify-between items-start">
                                <div className="flex items-center space-x-2">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    offer.user_type === 'mover' 
                                      ? 'bg-blue-500/20 text-blue-400' 
                                      : 'bg-yellow-500/20 text-yellow-400'
                                  }`}>
                                    {offer.user_type === 'mover' ? '🏢' : '👤'}
                                  </div>
                                  <div>
                                    <div className="font-semibold">
                                      {offer.user_type === 'mover' ? 'Votre proposition' : 'Proposition du client'}
                                    </div>
                                    <div className="text-sm text-gray-400">
                                      {new Date(offer.created_at).toLocaleString('fr-FR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-xl font-bold text-white">
                                    {parseFloat(offer.amount).toFixed(2)} TND
                                  </div>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getOfferStatusBadgeStyle(offer.status)}`}>
                                    {getStatusLabel(offer.status)}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Message de l'offre */}
                              {offer.message && (
                                <div className="mt-3 p-3 bg-gray-800/30 rounded-lg">
                                  <p className="text-sm text-gray-300">{offer.message}</p>
                                </div>
                              )}
                              
                              {/* Lien vers l'offre précédente si disponible */}
                              {offer.previous_offer_id && array.some(o => o.id === offer.previous_offer_id) && (
                                <div className="mt-2 text-xs text-gray-400">
                                  En réponse à une offre précédente
                                </div>
                              )}
                              
                              {/* Actions pour l'offre */}
                              {offer.status === 'pending' && offer.user_type !== 'mover' && (
                                <div className="mt-3 flex space-x-2">
                                  <button
                                    onClick={() => handleAcceptOffer(offer.id)}
                                    className="px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                                  >
                                    Accepter
                                  </button>
                                  <button
                                    onClick={() => handleRejectOffer(offer.id)}
                                    className="px-3 py-1.5 text-xs font-medium bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors"
                                  >
                                    Refuser
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-800/30 rounded-xl border border-dashed border-gray-700">
                        <div className="text-gray-500 mb-2">Aucune proposition pour le moment</div>
                        <div className="text-xs text-gray-600">Soyez le premier à faire une proposition</div>
                      </div>
                    )}
                  </div>

                  {/* Formulaire d'offre/contre-offre */}
                  {data?.status !== 'accepted' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">
                          {currentOffer ? 'Votre contre-proposition (TND)' : 'Votre proposition (TND)'}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={counterOffer}
                          onChange={(e) => setCounterOffer(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
                          placeholder={currentOffer ? 'Ex: 650.50' : 'Ex: 750.00'}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">
                          Message (optionnel)
                        </label>
                        <textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
                          placeholder={currentOffer ? 'Expliquez votre contre-proposition...' : 'Décrivez votre proposition...'}
                          rows="2"
                        />
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <button
                          onClick={handleSubmitCounterOffer}
                          disabled={!counterOffer || isSubmittingOffer}
                          className="flex-1 px-4 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-semibold rounded-xl hover:from-yellow-400 hover:to-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
                        >
                          {isSubmittingOffer ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                              Envoi en cours...
                            </>
                          ) : currentOffer ? (
                            'Envoyer la contre-proposition'
                          ) : (
                            'Faire une proposition'
                          )}
                        </button>
                        
                        {currentOffer && currentOffer.status === 'pending' && currentOffer.user_type !== 'client' && (
                          <button
                            onClick={() => handleAcceptOffer(currentOffer.id)}
                            disabled={isSubmittingOffer}
                            className="px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl hover:from-green-400 hover:to-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex-1"
                          >
                            Accepter l'offre
                          </button>
                        )}
                      </div>
                      
                      {currentOffer && currentOffer.status === 'pending' && currentOffer.user_type !== 'client' && (
                        <button
                          onClick={() => handleRejectOffer(currentOffer.id)}
                          disabled={isSubmittingOffer}
                          className="w-full px-4 py-2 text-sm text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Refuser cette offre
                        </button>
                      )}
                    </div>
                  )}
                  
                  {data?.status === 'accepted' && (
                    <div className="text-center py-4 bg-green-500/10 rounded-lg border border-green-500/20">
                      <div className="text-green-400 font-medium">Devis accepté</div>
                      <p className="text-sm text-green-300 mt-1">
                        Le prix convenu est de {data.price_cents / 100} TND
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mission Actions Card */}
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500/10 to-blue-600/5 border-b border-blue-500/20 p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <span className="text-blue-400">⚡</span>
                  </div>
                  <div className="text-blue-400 font-semibold">Actions de mission</div>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <button className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors">
                  📞 Contacter le client
                </button>

                {/* Conditional Action Buttons */}
                {(data?.status === "accepted" || data?.status === "confirmed") && (
                  <button
                    disabled={progressSaving}
                    onClick={async () => {
                      try {
                        setProgressSaving(true)
                        // Récupérer le move_id via l'endpoint by-quote
                        const moveResponse = await api.getMoverMoveByQuoteId(id)
                        const moveData = moveResponse?.data

                        if (moveData && moveData.id) {
                          const response = await api.moverStartMove(moveData.id)
                          if (response && response.data) {
                            // Mettre à jour l'état local avec la réponse de l'API
                            setData(prev => ({
                              ...prev,
                              status: 'in_progress',
                              move: {
                                ...prev.move,
                                status: 'in_progress',
                                started_at: new Date().toISOString()
                              }
                            }))
                          } else {
                            // Si la réponse n'est pas valide, recharger les données
                            const updatedQuote = await api.fetchMoverQuoteById(id)
                            setData(updatedQuote?.data || data)
                          }
                        } else {
                          alert("Aucun déménagement trouvé pour cette mission. Veuillez d'abord accepter la mission.")
                        }
                      } catch (e) {
                        console.error("Erreur démarrage:", e)
                        alert(e.message || "Erreur lors du démarrage de la mission")
                      } finally {
                        setProgressSaving(false)
                      }
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl hover:from-green-400 hover:to-green-500 disabled:opacity-50 transition-all duration-200"
                  >
                    {progressSaving ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Démarrage...</span>
                      </div>
                    ) : (
                      "🚀 Démarrer la mission"
                    )}
                  </button>
                )}

                {data?.status === "in_progress" && (
                  <button
                    disabled={progressSaving}
                    onClick={async () => {
                      try {
                        setProgressSaving(true)
                        // Récupérer le move_id via l'endpoint by-quote
                        const moveResponse = await api.getMoverMoveByQuoteId(id)
                        const moveData = moveResponse?.data

                        if (moveData && moveData.id) {
                          await api.moverCompleteMove(moveData.id)
                          // Recharger les données de la mission
                          const updatedQuote = await api.fetchMoverQuoteById(id)
                          setData(updatedQuote?.data || data)
                          // Pas d'alert, mise à jour silencieuse
                        } else {
                          alert("Aucun déménagement trouvé pour cette mission. Veuillez d'abord accepter la mission.")
                        }
                      } catch (e) {
                        console.error("Erreur terminaison:", e)
                        alert(e.message || "Erreur lors de la terminaison de la mission")
                      } finally {
                        setProgressSaving(false)
                      }
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-gray-700 to-gray-800 text-white font-semibold rounded-xl hover:from-gray-600 hover:to-gray-700 disabled:opacity-50 transition-all duration-200"
                  >
                    {progressSaving ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Finalisation...</span>
                      </div>
                    ) : (
                      "✅ Terminer la mission"
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Status Information Card */}
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-700/10 to-gray-600/5 border-b border-gray-500/20 p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-500/20 rounded-full flex items-center justify-center">
                    <span className="text-gray-400">ℹ️</span>
                  </div>
                  <div className="text-gray-400 font-semibold">Informations</div>
                </div>
              </div>
              <div className="p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Créé le:</span>
                  <span className="text-white">
                    {data.created_at ? new Date(data.created_at).toLocaleDateString("fr-FR") : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Mis à jour:</span>
                  <span className="text-white">
                    {data.updated_at ? new Date(data.updated_at).toLocaleDateString("fr-FR") : "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Status Messages */}
            {data?.status === "pending" && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <span className="text-yellow-400 text-xl">💡</span>
                  <div>
                    <div className="text-yellow-400 font-semibold text-sm">Mission en attente</div>
                    <div className="text-yellow-300 text-sm mt-1">
                      Proposez un prix pour accepter cette mission et accéder aux outils de suivi.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {data?.status === "completed" && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <span className="text-green-400 text-xl">🎉</span>
                  <div>
                    <div className="text-green-400 font-semibold">Mission terminée</div>
                    <div className="text-green-300 text-sm">Félicitations pour cette mission réussie !</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
